<template>
  <form class="form" data-test-id="form-email e-g-youremail-gmail-com" @submit.prevent="sendRequest">
    <fieldset>
      <legend>{{ legend }}</legend>
      <button
        v-for="(reason, index) in REASONS"
        :key="index"
        type="button"
        class="issue-btn"
        :class="{ active: selectedIssue === reason }"
        @click="toggleIssue(reason)"
      >
        {{ reasonLabels[reason] || reason }}
      </button>
    </fieldset>

    <label for="email">{{ emailLabel }}</label>
    <input
      id="email"
      v-model="email"
      type="email"
      maxlength="64"
      :placeholder="emailPlaceholder"
      :class="{ error: emailError }"
      @focus="clearErrors"
      @click="trackClick"
      @change="trackChange"
    />
    <div v-if="emailError" class="error-message">
      {{ emailError }}
    </div>

    <button type="submit" class="submit-btn" :disabled="!email">
      {{ submitLabel }}
    </button>
    <div class="divider">
      <span class="divider__text">or</span>
    </div>
    <SupportButton :label="supportLabel" />
  </form>
</template>

<script setup lang="ts">
import { ref, defineEmits, onMounted } from 'vue'

import '@/assets/styles/global.scss'

import { REASONS } from '@/constants/reasons'
import type { Reasons } from '@/constants/reasons'
import TrackingService from '@/services/TrackingService'
import { ActionType, SourceObjectValueElementSubtype, EventSubtypes, SourceObjectKey } from '@/types/events'
import type { FormPayload } from '@/types/form'
import { getEmailError } from '@/utils/validation/email'

import SupportButton from './SupportButton.vue'

withDefaults(defineProps<{
  legend?: string
  emailLabel?: string
  emailPlaceholder?: string
  submitLabel?: string
  supportLabel?: string
  reasonLabels?: Partial<Record<Reasons, string>>
}>(), {
  legend: 'Choose your issue',
  emailLabel: 'Provide your email address *',
  emailPlaceholder: 'example@gmail.com',
  submitLabel: 'Send Request',
  supportLabel: 'Instant Support',
  reasonLabels: () => ({}),
})

const emit = defineEmits<{(e: 'formSubmitted', payload: FormPayload): void }>()

const selectedIssue = ref<Reasons | null>(REASONS[0])
const email = ref('')
const emailError = ref('')

function toggleIssue(issue: Reasons): void {
  if (selectedIssue.value === issue) {
    selectedIssue.value = null

    return
  }
  selectedIssue.value = issue
  TrackingService.trackComponentInteraction(
    ActionType.Click,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.IssueType,
      },
      {
        key: SourceObjectKey.IssueSubtype,
        value: issue,
      },
    ],
    EventSubtypes.DescriptorForm,
  )
}

const trackClick = (): void => {
  TrackingService.trackComponentInteraction(
    ActionType.Click,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.Email,
      },
    ],
    EventSubtypes.DescriptorForm,
  )
}

const trackChange = (): void => {
  TrackingService.trackComponentInteraction(
    ActionType.Change,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.Email,
      },
    ],
    EventSubtypes.DescriptorFormInteraction,
  )
}

function clearErrors(): void {
  emailError.value = ''
}

function sendRequest(): void {
  emailError.value = getEmailError(email.value)

  if (emailError.value) {
    return
  }

  const payload: FormPayload = {
    email: email.value,
    reason: selectedIssue.value || undefined,
  }

  emit('formSubmitted', payload)

  TrackingService.trackComponentInteraction(
    ActionType.Click,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.SendRequest,
      },
      {
        key: SourceObjectKey.Email,
        value: email.value,
      },
      {
        key: SourceObjectKey.IssueSubtype,
        value: selectedIssue.value || '',
      },
    ],
    EventSubtypes.DescriptorForm,
  )

  reset()
}

function reset(): void {
  email.value = ''
  // eslint-disable-next-line prefer-destructuring
  selectedIssue.value = REASONS[0]
  clearErrors()
}

onMounted((): void => {
  TrackingService.trackComponentInteraction(
    ActionType.Show,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.DescriptorForm,
      },
    ],
    EventSubtypes.DescriptorForm,
  )
})
</script>

<style scoped lang="scss">
.form {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
  user-select: none;
  border-radius: 12px;
  background: var(--color-bg-form);

  fieldset {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin: 0 0 24px;
    padding: 0;
    border: none;
    font-weight: var(--font-weight-regular);

    legend {
      margin-bottom: 12px;
      color: var(--color-text);
      font-size: 16px;
      font-weight: var(--font-weight-bold);
    }

    @media (max-width: 480px) {
      justify-content: flex-start;
    }
  }

  .issue-btn {
    padding: 12px 20px;
    cursor: pointer;
    transition: background-color 0.3s, border-color 0.3s;
    color: var(--color-text);
    border: 1px solid var(--color-border);
    border-radius: 8px;
    background: white;
    font-size: 14px;
    font-weight: var(--font-weight-regular);

    &:hover {
      border-color: #43977e66;
    }

    &.active {
      color: var(--color-primary);
      border: 1.5px solid var(--color-primary);
    }

    @media (max-width: 480px) {
      text-align: left;
    }
  }

  .buttons-row {
    &.half-width {
      display: flex;
      width: 50%;
      gap: 4px;

      @media (max-width: 768px) {
        width: 40%;
      }
    }

    &.full-width {
      @media (max-width: 768px) {
        flex-direction: column;
        width: 80%;
      }
    }

    &.full-width > button,
    &.half-width > button {
      @media (max-width: 768px) {
        flex: none;
        width: 100%;
      }
    }
  }

  label {
    display: block;
    margin-bottom: 12px;
    color: var(--color-text);
    font-size: 16px;
    font-weight: var(--font-weight-bold);
  }

  input[type='email'] {
    box-sizing: border-box;
    width: 100%;
    padding: 1rem;
    user-select: text;
    border: 1px solid var(--color-border);
    border-radius: 8px;
    font-size: 12px;

    &:hover {
      border-color: var(--color-border-hover);
      outline: none;
    }

    &:focus {
      border-color: var(--color-border);
      outline: none;
    }

    &.error {
      border-color: var(--color-error);
    }

    &::placeholder {
      color: var(--color-placeholder, #adadad);
      font-size: 14px;
      font-weight: var(--font-weight-regular);
    }
  }

  .error-message {
    min-height: 16px;
    padding-top: 4px;
    color: var(--color-error-text);
    font-size: 12px;
    font-weight: var(--font-weight-regular);
  }

  .submit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 100%;
    min-height: 44px;
    margin-top: 32px;
    padding: 12px 8px;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.3s ease;
    color: white;
    border: none;
    border-radius: var(--button-corner-radius, 8px);
    background-color: var(--color-primary);
    font-size: 16px;
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height);
    gap: 8px;

    &:hover {
      background-color: var(--color-primary-hover);
    }

    &:active {
      background-color: var(--color-primary-active);
    }

    &:disabled {
      cursor: not-allowed;
      border-radius: var(--button-corner-radius, 8px);
      background: var(--color-disabled-bg);
    }
    @media (max-width: 768px) {
      min-height: 48px;
      padding: 12px 8px;
    }
  }
}

.divider {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 191px;
  margin: 8px auto;
  text-align: center;
  color: var(--color-border-primary);

  &__text {
    padding: 0 10px;
  }
}

.divider:before,
.divider:after {
  flex-grow: 1;
  height: 1px;
  content: '';
  background-color: var(--color-border-primary);
}
</style>
